import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, PlusCircle, Trash, Save, Download } from "lucide-react";
import writtenNumber from "written-number";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import type { TransactionData } from "@/types/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useDownloadPDF } from "@/hooks/useMobileDownload";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/userStore";
import {
  loadGymInvoiceIndex,
  findInvoiceInIndex,
  computeNextInvoiceNumberForGym,
  buildInvoiceFileName,
  uploadInvoice,
  replaceInvoice,
  invoiceObjectPath,
  type GymInvoiceIndex,
} from "@/services/invoicesStorage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  getInvoiceConfig,
  LEGACY_INVOICE_CONFIG,
  type InvoiceConfig,
} from "@/services/invoiceConfigService";
import { useSettingsStore } from "@/store/api/settingsStore";


pdfMake.vfs = pdfFonts?.default?.pdfMake?.vfs || pdfFonts?.pdfMake?.vfs;
writtenNumber.defaults.lang = 'fr';

const FALLBACK_LOGO_URL = "/lovable-uploads/vb-logo-white.png";

function formatCurrencyClean(amount) {
  return amount
    .toLocaleString("fr-TN", {
      style: "currency",
      currency: "TND",
      minimumFractionDigits: 3,
    })
    .replace(/\u202F/g, " ")
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace("TND", "DT");
}

interface InvoiceLine {
  designation: string;
  quantity: number;
  unitPrice: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionData: TransactionData | null;
}

export function InvoicePreviewDialog({
  open,
  onOpenChange,
  transactionData,
}: Props) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [stamp, setStamp] = useState(1); // timbre fiscal
  const printRef = useRef<HTMLDivElement>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [fileName, setFileName] = useState("");
  // Chemin complet de la facture déjà émise (dossier de la salle inclus) :
  // c'est lui qu'on signe, télécharge et remplace.
  const [invoicePath, setInvoicePath] = useState("");
  // Mode correction d'une facture existante : numéro figé, PDF remplacé.
  const [isEditing, setIsEditing] = useState(false);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);
  const [invoiceConfig, setInvoiceConfig] = useState<InvoiceConfig>(LEGACY_INVOICE_CONFIG);
  const [ccClient, setCcClient] = useState(false);
  const role = useUserStore((state) => state.role);
  const isAdmin = role === "admin" || role === "super_admin";
  const { t } = useTranslation(["payments", "bookingui"]);
  const settings = useSettingsStore((state) => state.settings);
  const fetchSettings = useSettingsStore((state) => state.fetchSettings);
  const loadedSettingsGymId = useSettingsStore((state) => state.loadedGymId);
  const invoiceLogoUrl = settings.appearance.logoLightUrl?.trim()
    || settings.appearance.logoDarkUrl?.trim()
    || FALLBACK_LOGO_URL;


  const { user } = useAuth();

  const { isDownloading, error, download, downloadFromUrl } = useDownloadPDF();

  // Informations émetteur / destinataire configurées par la salle
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const gymId = useUserStore.getState().gymId;
    if (!gymId) return;
    if (loadedSettingsGymId !== gymId) {
      void fetchSettings();
    }
    getInvoiceConfig(gymId)
      .then((config) => {
        if (mounted) setInvoiceConfig(config);
      })
      .catch((err) => console.error("[InvoicePreviewDialog] invoice_config illisible:", err));
    setCcClient(false);
    return () => {
      mounted = false;
    };
  }, [fetchSettings, loadedSettingsGymId, open]);


  // Remplissage auto à l'ouverture
  useEffect(() => {
    if (open && transactionData) {
      const checkExistingInvoice = async () => {
        const gymId = useUserStore.getState().gymId;

        // La numérotation est propre à chaque salle : on ne lit que le dossier
        // de la salle connectée (le reliquat racine sert seulement à retrouver
        // les factures déjà émises).
        let index: GymInvoiceIndex;
        try {
          index = await loadGymInvoiceIndex(gymId);
        } catch (err) {
          console.error('[InvoicePreviewDialog] Lecture du bucket invoices impossible:', err);
          toast.error(t("invoice.fetchExistingError", { ns: "bookingui" }));
          return;
        }

        setIsEditing(false);
        setConfirmReplaceOpen(false);

        // Les champs saisissables sont toujours préremplis : ils servent aussi
        // de point de départ à la correction d'une facture déjà émise.
        setInvoiceDate(transactionData.date?.slice(0, 10) || new Date().toISOString().slice(0, 10));
        setClientName(transactionData.client || "");
        setClientAddress("");
        // "transfer" est l'ancien libellé : on l'aligne sur bank_transfer.
        setPaymentMode(transactionData.paymentMethod === "transfer" ? "bank_transfer" : (transactionData.paymentMethod || ""));
        setLines([
          {
            designation: transactionData.description || transactionData.type || "Prestation",
            quantity: 1,
            unitPrice: transactionData.amount || 0,
          },
        ]);

        // Rapprochement sur l'identifiant complet, avec repli sur l'ancienne
        // convention tronquée à 8 caractères (cf. invoicesStorage) : deux
        // abonnements dont l'identifiant commençait pareil ne se confondent
        // plus, et les factures déjà émises restent retrouvables.
        const existing = findInvoiceInIndex(index, transactionData.id.toString());
        if (existing) {
          setFileName(existing.file.name);
          setInvoicePath(existing.path);
          setInvoiceNumber(existing.file.name.match(/_(\d{4}-\d{3,})\.pdf$/)?.[1] ?? "");
          setIsLocked(true);
          // Génère l'URL (public ou signed, selon config)
          const { data: urlData } = await supabase.storage
            .from('invoices')
            .createSignedUrl(existing.path, 60 * 60);
          setPdfUrl(urlData?.signedUrl ?? null);
        } else {
          setIsLocked(false);
          setPdfUrl(null);
          // Numéro calculé sur le seul dossier de la salle ; il sera de toute
          // façon recalculé au moment de l'envoi (cf. handleSaveToSupabase).
          const nextNumber = computeNextInvoiceNumberForGym(index);
          setInvoiceNumber(nextNumber);
          // Nom pressenti, à titre indicatif : `uploadInvoice` le reconstruira
          // avec le numéro réellement réservé au moment de l'envoi.
          const nextFileName = buildInvoiceFileName(transactionData.id.toString(), nextNumber);
          setFileName(nextFileName);
          setInvoicePath(invoiceObjectPath(gymId, nextFileName));
        }
      };
      checkExistingInvoice();
    }
  }, [open, t, transactionData]);
  
  
  const totalHT = useMemo(() => lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0), [lines]);
  const totalTTC = totalHT + stamp;
  const totalText = useMemo(() =>
    writtenNumber(totalTTC),
    [totalTTC]);

  const issuerFooterText = useMemo(() => {
    const firstLine = [invoiceConfig.companyName, invoiceConfig.companyAddress]
      .filter(Boolean)
      .join(" – ");
    const secondLine = invoiceConfig.companyTaxId
      ? `Matricule fiscal ${invoiceConfig.companyTaxId}`
      : "";
    return [firstLine, secondLine].filter(Boolean).join("\n");
  }, [invoiceConfig]);

  const handleLineChange = (i: number, field: keyof InvoiceLine, value: any) => {
    setLines(lines => lines.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  };
  const addLine = () => setLines(lines => [...lines, { designation: "", quantity: 1, unitPrice: 0 }]);
  const removeLine = (i: number) => setLines(lines => lines.filter((_, idx) => idx !== i));

  // PDF
  function getBase64ImageFromUrl(url: string): Promise<string> {
    return fetch(url)
      .then(response => response.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }));
  }

  async function getInvoiceLogoBase64(): Promise<string> {
    const currentAppearance = useSettingsStore.getState().settings.appearance;
    const savedLogoUrl = currentAppearance.logoLightUrl?.trim()
      || currentAppearance.logoDarkUrl?.trim();

    if (savedLogoUrl) {
      try {
        return await getBase64ImageFromUrl(savedLogoUrl);
      } catch (logoError) {
        console.warn("[InvoicePreviewDialog] Logo de la salle illisible, utilisation du logo de secours:", logoError);
      }
    }

    return getBase64ImageFromUrl(FALLBACK_LOGO_URL);
  }

  const handleSaveToSupabase = async () => {
    if (!transactionData) return;
    try {
      setIsSaving(true);
      const logoBase64 = await getInvoiceLogoBase64();

      // Le numéro est (re)calculé sur le dossier de la salle juste avant
      // l'envoi, et `upsert: false` sert de garde-fou : si un autre poste a
      // pris le numéro entre l'ouverture de l'aperçu et l'enregistrement, on
      // recalcule au lieu de réattribuer un numéro déjà émis.
      let generatedBlob: Blob | null = null;
      const uploaded = await uploadInvoice(
        useUserStore.getState().gymId,
        transactionData.id.toString(),
        async (number) => {
          setInvoiceNumber(number);
          generatedBlob = await new Promise<Blob>((resolve) => {
            pdfMake.createPdf(buildPdfContent(logoBase64, number)).getBlob(resolve);
          });
          return generatedBlob;
        }
      );

      setFileName(uploaded.fileName);
      setInvoicePath(uploaded.path);

      if (uploaded.alreadyExisted) {
        // Une facture existait déjà pour cette transaction : on ne prétend pas
        // en avoir créé une seconde, on affiche celle qui fait foi.
        setIsLocked(true);
        const { data: urlData } = await supabase.storage
          .from("invoices")
          .createSignedUrl(uploaded.path, 60 * 60);
        setPdfUrl(urlData?.signedUrl ?? null);
        toast.info(t("invoice.alreadyExists", { ns: "bookingui", fileName: uploaded.fileName }));
        return;
      }

      toast.success(t("invoice.saveSuccess", { ns: "bookingui" }));
      if (generatedBlob) {
        await download(generatedBlob, uploaded.fileName);
      }
    } catch (error: any) {
      console.error("[InvoicePreviewDialog] Envoi de la facture échoué:", error);
      toast.error(t("invoice.uploadError", { ns: "bookingui", message: error?.message ?? "erreur inconnue" }));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Correction d'une facture déjà émise : le PDF est remplacé, le numéro et le
   * chemin restent identiques (sinon la série comporterait un trou et deux PDF
   * porteraient le même numéro). Réservé aux administrateurs, contrôlé aussi
   * par la politique storage côté serveur.
   */
  const handleReplaceInvoice = async () => {
    if (!transactionData || !invoicePath) return;
    try {
      setIsSaving(true);
      const logoBase64 = await getInvoiceLogoBase64();
      const pdfBlob = await new Promise<Blob>((resolve) => {
        pdfMake.createPdf(buildPdfContent(logoBase64, invoiceNumber)).getBlob(resolve);
      });

      await replaceInvoice(invoicePath, pdfBlob);

      const { data: urlData } = await supabase.storage
        .from("invoices")
        .createSignedUrl(invoicePath, 60 * 60, { download: false });
      setPdfUrl(urlData?.signedUrl ?? null);
      setIsEditing(false);
      setIsLocked(true);
      toast.success(t("invoice.replaceSuccess", { ns: "bookingui" }));
      await download(pdfBlob, fileName);
    } catch (error: any) {
      console.error("[InvoicePreviewDialog] Remplacement de la facture échoué:", error);
      toast.error(t("invoice.replaceError", { ns: "bookingui", message: error?.message ?? "erreur inconnue" }));
    } finally {
      setIsSaving(false);
      setConfirmReplaceOpen(false);
    }
  };

  const handleSendEmail = async () => {
    if (!user?.email) {
      toast.error(t("invoice.adminEmailMissing", { ns: "bookingui" }));
      return;
    }

    const recipientEmail = invoiceConfig.recipientEmail.trim();
    if (!recipientEmail) {
      toast.error(t("invoice.recipientMissing", { ns: "bookingui" }));
      return;
    }

    setIsSendingEmail(true);
    
    try {
      const logoBase64 = await getInvoiceLogoBase64();
      const pdfBlob = await new Promise<Blob>((resolve) => {
        pdfMake.createPdf(buildPdfContent(logoBase64)).getBlob(resolve);
      });

      // Convert blob to base64
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

      const invoiceData = {
        invoiceNumber,
        invoiceDate,
        clientName: clientName || transactionData?.client || t("invoice.unknownClient", { ns: "bookingui" }),
        clientAddress,
        totalAmount: totalTTC,
        transactionId: transactionData?.id?.toString() || "N/A"
      };

      const gymId = useUserStore.getState().gymId;
      
      const { data, error } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          carbonCopy: [user.email],
          recipient: recipientEmail,
          cc_client: ccClient,
          client_user_id: transactionData?.userId ?? null,
          pdfBase64: base64,
          invoiceData,
          gym_id: gymId
        }
      });

      if (error) {
        console.error('Error sending invoice email:', error);
        toast.error(t("invoice.emailError", { ns: "bookingui" }));
      } else {
        toast.success(t("invoice.emailSuccess", { ns: "bookingui" }));
      }
    } catch (error) {
      console.error('Error generating PDF or sending email:', error);
      toast.error(t("invoice.generationError", { ns: "bookingui" }));
    } finally {
      setIsSendingEmail(false);
    }
  };

  const buildPdfContent = (logoBase64, numberOverride?: string) => {
    // Le numéro imprimé doit être celui réellement réservé au moment de
    // l'envoi, pas celui affiché à l'ouverture du dialogue.
    const printedNumber = numberOverride ?? invoiceNumber;
    const filteredLines = lines.filter(
      l =>
        l &&
        typeof l.designation === "string" &&
        l.designation.trim().length > 0 &&
        typeof l.quantity === "number" &&
        typeof l.unitPrice === "number"
    );
    
    const tableBody = [
      [
        { text: "DESIGNATION", fillColor: "#ffeb8a", bold: true },
        { text: "QTE", fillColor: "#ffeb8a", bold: true },
        { text: "PRIX UNIT TTC", fillColor: "#ffeb8a", bold: true },
        { text: "MONTANT TTC", fillColor: "#ffeb8a", bold: true }
      ],
      ...filteredLines.map(l => [
        l.designation,
        l.quantity,
        formatCurrencyClean(l.unitPrice),
        formatCurrencyClean(l.quantity * l.unitPrice)
      ])
    ];
  
    return {
      content: [
        { image: logoBase64, width: 70, alignment: 'left' },
        {
          columns: [
            [
              { text: invoiceConfig.companyName, bold: true, fontSize: 14, margin: [0, 4] },
              { text: invoiceConfig.companyAddress, fontSize: 9 },
              { text: invoiceConfig.companyTaxId ? `Matricule fiscal ${invoiceConfig.companyTaxId}` : "", fontSize: 9 },
            ],
            [
              { text: clientAddress || "Nom & Adresse du client", fontSize: 9, alignment: "right" }
            ]
          ]
        },
        { text: "Facture", alignment: "center", fontSize: 16, bold: true, margin: [0, 18, 0, 8] },
        {
          columns: [
            [{ text: `Facture n° : ${printedNumber}`, fontSize: 10 }, { text: `Date : ${invoiceDate}`, fontSize: 10 }]
          ],
          margin: [0, 0, 0, 8]
        },
        {
          table: {
            widths: ['*', 35, 70, 90],
            headerRows: 1,
            body:tableBody
          },
          margin: [0, 0, 0, 8]
        },
        {
          columns: [
            { width: "*", text: "" },
            {
              width: "auto",
              table: {
                body: [
                  ["TOTAL HT", formatCurrencyClean(totalHT)],
                  ["TVA (0%)", "0,000"],
                  ["Timbre fiscal", formatCurrencyClean(stamp)],
                  [
                    { text: "TOTAL TTC", fillColor: "#ffeb8a", bold: true },
                    formatCurrencyClean(totalTTC)
                  ]
                ]
              },
              layout: "lightHorizontalLines",
              margin: [0, 0, 0, 0]
            }
          ]
        },   
        {
          text: `${t("paymentMethod")} : ${paymentMode ? t("methods." + paymentMode, { defaultValue: paymentMode }) : "-"}`,
          fontSize: 10,
          margin: [0, 8, 0, 0],
        },
        { text: `${t("invoice.settledAmount", { ns: "bookingui" })}\n${totalText} dinars`, fontSize: 10, margin: [0, 14, 0, 0] },
        {
          columns: [
            { width: '*', text: '' },
            {
              width: 'auto',
              text: 'Signature et cachet',
              fontSize: 10,
              italics: true,
              alignment: 'right',
              margin: [0, 0, 0, 10]
            }
          ]
        }
      ],
      footer: {
        columns: [
          { text: issuerFooterText, fontSize: 8, alignment: 'center', margin: [0, 10] }
        ]
      }
    };
  };

  if (!transactionData) return null;

  
  // ----------------- MODE INTERACTIF NORMAL -------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[900px] max-h-[96vh] p-4 overflow-auto bg-card text-card-foreground">
      <DialogHeader>
      {isLocked && pdfUrl ? <DialogTitle>{t("invoice.title", { ns: "bookingui" })}</DialogTitle>
        : <DialogTitle>{t("invoice.previewTitle", { ns: "bookingui" })}</DialogTitle>}
      </DialogHeader>
      <div
        ref={printRef}
        className="invoice-paper mx-auto p-6 rounded-lg shadow-md w-full print:w-full print:shadow-none print:p-2"
        style={{ maxWidth: 850, fontFamily: "Inter, Arial, sans-serif" }}
      >
        <div className="flex flex-wrap justify-between mb-4">
          <div>
            <img src={invoiceLogoUrl} alt={invoiceConfig.companyName || "Logo"} className="h-12 max-w-48 object-contain object-left mb-1" />
            <div className="font-bold text-base">{invoiceConfig.companyName}</div>
            <div className="text-xs">{invoiceConfig.companyAddress}</div>
            {invoiceConfig.companyTaxId && (
              <div className="text-xs">Matricule fiscal {invoiceConfig.companyTaxId}</div>
            )}
          </div>
        </div>
        <Separator />
        <div className="flex flex-wrap justify-between items-center my-3">
          <div>
            <div className="font-bold text-xl tracking-tight">Facture</div>
            {/* En correction, le numéro est figé : le modifier créerait un
                doublon ou un trou dans la série de la salle. */}
            <div className="text-xs mt-2">Facture n° : <Input className="inline-block w-28 text-xs" value={invoiceNumber} readOnly={isEditing} onChange={e => setInvoiceNumber(e.target.value)} /></div>
            <div className="text-xs">Date : <Input className="inline-block w-28 text-xs" type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} /></div>
            <div className="text-xs mt-2 flex flex-wrap items-center gap-2">
              <span>{t("paymentMethod")} :</span>
              <Select value={paymentMode || undefined} onValueChange={setPaymentMode}>
                <SelectTrigger className="h-8 w-44 text-xs">
                  <SelectValue placeholder={t("selectPaymentMethod")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">{t("methods.card")}</SelectItem>
                  <SelectItem value="cash">{t("methods.cash")}</SelectItem>
                  <SelectItem value="bank_transfer">{t("methods.bank_transfer")}</SelectItem>
                  <SelectItem value="check">{t("methods.check")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>

            <Textarea className="bg-status-warning text-status-warning-foreground rounded-lg px-4 py-2 text-right font-semibold shadow border border-status-warning-border" 
            placeholder={t("invoice.clientAddressPlaceholder", { ns: "bookingui" })} rows={2} 
            value={clientAddress} onChange={e => setClientAddress(e.target.value)} />
          </div>
        </div>
        <table className="w-full border mt-4 mb-4 text-xs">
          <thead>
            <tr className="bg-status-warning text-status-warning-foreground font-semibold text-xs">
              <th className="border p-1">DESIGNATION</th>
              <th className="border p-1">QTE</th>
              <th className="border p-1">PRIX UNIT TTC</th>
              <th className="border p-1">MONTANT TTC</th>
              <th className="border p-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td className="border p-1">
                  <Input value={line.designation} onChange={e => handleLineChange(i, "designation", e.target.value)} className="text-xs" />
                </td>
                <td className="border p-1 w-28">
                  <Input type="number" min={1} value={line.quantity} onChange={e => handleLineChange(i, "quantity", Number(e.target.value))} className="text-xs" />
                </td>
                <td className="border p-1 w-28">
                  <Input type="number" min={0} value={line.unitPrice} onChange={e => handleLineChange(i, "unitPrice", Number(e.target.value))} className="text-xs" />
                </td>
                <td className="border p-1 text-right w-32">{formatCurrencyClean(line.quantity * line.unitPrice)}</td>
                <td className="border p-1 text-center w-8">
                  {lines.length > 1 &&
                    <Button size="icon" variant="ghost" onClick={() => removeLine(i)}><Trash className="w-4 h-4 text-red-500" /></Button>
                  }
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={5} className="p-1">
                <Button type="button" size="sm" variant="ghost" onClick={addLine}>
                  <PlusCircle className="w-4 h-4 mr-1" /> {t("invoice.addLine", { ns: "bookingui" })}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="flex flex-wrap justify-end">
          <table className="text-sm mb-3" style={{ minWidth: 270 }}>
            <tbody>
              <tr>
                <td className="pr-2 text-right">TOTAL HT</td>
                <td className="pl-2 text-right">{formatCurrencyClean(totalHT)}</td>
              </tr>
              <tr>
                <td className="pr-2 text-right">TVA</td>
                <td className="pl-2 text-right">0%</td>
                <td className="pl-2 text-right">0,000</td>
              </tr>
              <tr>
                <td className="pr-2 text-right">Timbre fiscal</td>
                <td className="pl-2 text-right" colSpan={2}>
                  <Input type="number" min={0} className="inline-block w-20" value={stamp} onChange={e => setStamp(Number(e.target.value))} />
                </td>
              </tr>
              <tr className="bg-status-warning text-status-warning-foreground font-bold">
                <td className="pr-2 text-right">TOTAL TTC</td>
                <td className="pl-2 text-right" colSpan={2}>{formatCurrencyClean(totalTTC)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="my-3 text-xs">{t("invoice.settledAmount", { ns: "bookingui" })}<br /><span className="font-semibold">{totalText} dinars</span></div>
        <Separator className="my-2" />
        <div className="text-xs text-muted-foreground text-right mt-6 whitespace-pre-line">
          {issuerFooterText}
        </div>
      </div>
      <DialogFooter className="flex flex-col gap-2 mt-4 sm:flex-row sm:flex-wrap sm:justify-end sm:items-center">
        <div className="flex items-center gap-2 sm:mr-auto">
          <Checkbox
            id="invoice-cc-client"
            checked={ccClient}
            onCheckedChange={(checked) => setCcClient(checked === true)}
          />
          <Label htmlFor="invoice-cc-client" className="text-sm font-normal">
            {t("invoice.ccClient", { ns: "bookingui" })}
          </Label>
        </div>

        <Button className="w-full sm:w-auto" onClick={handleSendEmail} disabled={isSendingEmail}>
          <Mail className="h-4 w-4 mr-1" /> {isSendingEmail ? t("invoice.sendingEmail", { ns: "bookingui" }) : t("invoice.sendEmail", { ns: "bookingui" })}
        </Button>

        {/* Facture déjà émise : correction réservée aux administrateurs. */}
        {isLocked && !isEditing && isAdmin && (
          <Button className="w-full sm:w-auto" variant="outline" onClick={() => setIsEditing(true)}>
            <Pencil className="h-4 w-4 mr-1" /> {t("invoice.edit", { ns: "bookingui" })}
          </Button>
        )}

        {isEditing ? (
          <>
            <Button
              className="w-full sm:w-auto"
              variant="ghost"
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
            >
              {t("invoice.cancelEdit", { ns: "bookingui" })}
            </Button>
            <Button
              className="w-full sm:w-auto"
              variant="default"
              onClick={() => setConfirmReplaceOpen(true)}
              disabled={isSaving}
            >
              <Save className="h-4 w-4 mr-1" /> {isSaving ? t("invoice.saving", { ns: "bookingui" }) : t("invoice.saveChanges", { ns: "bookingui" })}
            </Button>
          </>
        ) : (
          <Button
            className="w-full sm:w-auto"
            variant="default"
            onClick={handleSaveToSupabase}
            disabled={isSaving || (isLocked && pdfUrl ? true : false)}
          >
            <Save className="h-4 w-4 mr-1" /> {isSaving ? t("invoice.saving", { ns: "bookingui" }) : t("invoice.save", { ns: "bookingui" })}
          </Button>
        )}

        {isLocked && pdfUrl && !isEditing && (
            <Button
              className="w-full sm:w-auto"
              onClick={() => downloadFromUrl(pdfUrl, fileName)}
              variant="default"
              >
              <Download className="h-4 w-4 mr-1" /> {isDownloading ? t("invoice.downloading", { ns: "bookingui" }) : t("invoice.download", { ns: "bookingui" })}
            </Button>
        )}

      </DialogFooter>

      <AlertDialog open={confirmReplaceOpen} onOpenChange={setConfirmReplaceOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoice.replaceConfirmTitle", { ns: "bookingui" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("invoice.replaceConfirmDescription", { ns: "bookingui", number: invoiceNumber })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>{t("invoice.cancelEdit", { ns: "bookingui" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReplaceInvoice} disabled={isSaving}>
              {t("invoice.replaceConfirmAction", { ns: "bookingui" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  </Dialog>
  );
}
